import { Collection, Plugin } from 'ckeditor5';
import { CONDITION_INLINE_PREFIX, parseMarkerName } from './markerName';
/**
 * Whether a marker still points into the document the user can see.
 *
 * `$graveyard` is where CKEditor parks deleted content so undo can restore it. Markers that went with
 * it stay registered in `model.markers`, so the root has to be checked rather than assumed.
 */
function isInLiveRoot(marker) {
    const { root } = marker.getRange();
    return root.is('rootElement') && root.rootName !== '$graveyard';
}
/**
 * Tracks the conditions in the editor.
 *
 * Two kinds of state live here, answering different questions:
 *
 * - `conditionData` is a log. An entry flips to `inDocument: false` when its marker is
 *   removed and is never dropped, because that is how the template screens detect "the author deleted
 *   a condition" and block autosave until it is undone.
 * - The query helpers (like `getConditionsInDocument`) read `editor.model.markers`, which is
 *   the truth for what is *currently* in the document, so they never consult the log.
 */
export class ConditionState extends Plugin {
    constructor() {
        super(...arguments);
        this.conditionData = new Collection();
        /**
         * Marker names currently being renamed. A rename is a remove + add, and without this the remove
         * would leave a permanent `inDocument: false` tombstone that reads as a deletion.
         */
        this.renaming = new Set();
    }
    static get pluginName() {
        return 'ConditionState';
    }
    init() {
        this.trackMarkerChanges();
        this.set('activeCondition', undefined);
    }
    trackMarkerChanges() {
        const { conversion } = this.editor;
        conversion.for('downcast').add((dispatcher) => {
            dispatcher.on('addMarker:condition-inline', (_, data) => {
                this.addCondition(data.markerName);
            });
            dispatcher.on('removeMarker:condition-inline', (_, data) => {
                const { markerName } = data;
                if (this.renaming.has(markerName)) {
                    this.renaming.delete(markerName);
                    this.removeCondition(markerName);
                    return;
                }
                this.setInDocument(markerName, false);
            });
        });
    }
    /**
     * Declares that `markerName` is about to be replaced by an equivalent marker under a new name, so
     * its removal should drop the entry rather than tombstone it. Call before the `model.change()`.
     */
    markRenaming(markerName) {
        this.renaming.add(markerName);
    }
    addCondition(id) {
        if (!id) {
            return;
        }
        const existing = this.conditionData.get(id);
        if (existing) {
            existing.inDocument = true;
            return;
        }
        this.conditionData.add({ id, inDocument: true });
    }
    setInDocument(id, inDocument) {
        const item = this.conditionData.get(id);
        if (item) {
            item.inDocument = inDocument;
        }
    }
    getCondition(id) {
        if (!id) {
            return null;
        }
        return this.conditionData.get(id);
    }
    getActiveCondition() {
        if (!this.activeCondition) {
            return null;
        }
        return parseMarkerName(this.activeCondition);
    }
    unsetActiveCondition() {
        this.activeCondition = undefined;
    }
    /** Drops an entry entirely, entry in conditionData log included. */
    removeCondition(id) {
        if (this.conditionData.has(id)) {
            this.conditionData.remove(id);
        }
    }
    /**
     * Every inline condition currently in the document, parsed.
     *
     * Only the **live** markers — see `isInLiveRoot`. Without that filter, deleting a paragraph leaves
     * its condition reported as present, and replacing the document (which is what importing does)
     * reports every condition of the previous one, each as a phantom error.
     */
    getConditionsInDocument() {
        return this.getMarkersInDocument()
            .map((marker) => parseMarkerName(marker.name))
            .filter((parsed) => parsed !== null);
    }
    getMarkersInDocument() {
        const { markers } = this.editor.model;
        return Array.from(markers.getMarkersGroup(CONDITION_INLINE_PREFIX)).filter(isInLiveRoot);
    }
    /** Distinct, non-empty question refs referenced by conditions in the document. */
    getAllConditionRefs() {
        const refs = this.getConditionsInDocument()
            .map(({ ref }) => ref)
            .filter(Boolean);
        return [...new Set(refs)];
    }
    countConditionsForRef(ref) {
        return this.getConditionsInDocument().filter((condition) => condition.ref === ref).length;
    }
}
